"use client";

import { useEffect, useState } from "react";

import {
  checkMedicationInItemTable,
  getPatientRxNumbers,
  getPrescriptionMedications,
  searchPatients,
} from "../../../../api/dispensary/script/LabelApi.js";

import { fetchScriptDetails } from "../../../../api/dispensary/script/processScriptApi.js";

// Helper function to format patient name for display (firstname + surname only)
const formatPatientNameForDisplay = (patientName) => {
  if (!patientName) return "";

  // Parse name to get title, firstname, surname
  const nameParts = patientName.trim().split(/\s+/);
  if (nameParts.length <= 2) return patientName; // Already just firstname surname or single name

  // Remove title and get firstname + surname
  const titlePatterns = ["MR", "MISS", "MRS", "MS", "DR", "PROF"];
  const hasTitle = titlePatterns.includes(nameParts[0].toUpperCase());

  if (hasTitle && nameParts.length >= 3) {
    // Format: "John Smith" from "Mr John Smith M001/123456"
    const firstname = nameParts[1];
    const surname = nameParts[2].split("/")[0]; // Remove any codes after surname
    return `${firstname} ${surname}`;
  } else if (nameParts.length >= 2) {
    // Format: "John Smith" from "John Smith M001/123456"
    const firstname = nameParts[0];
    const surname = nameParts[1].split("/")[0]; // Remove any codes after surname
    return `${firstname} ${surname}`;
  }

  return patientName.split("/")[0]; // Fallback: remove everything after first slash
};

export default function useMedicationFormCard({
  form,
  onFieldChange,
  prescriptionId,
  initialMedications,
  scriptNumber,
  isFromDispensaryMenu,
  pharmacyInfo,
  user,
  drugInfo,
  showRepeat = false,
  repeatData = null,
  repeatNumber = null,
}) {
  const [rxNumbers, setRxNumbers] = useState([]);

  const [selectedPatient, setSelectedPatient] = useState(null);

  const [medications, setMedications] = useState([]);

  const [selectedRxNumber, setSelectedRxNumber] = useState(null);

  const [icd10Codes, setIcd10Codes] = useState([]);

  const [selectedMedication, setSelectedMedication] = useState(null);

  const [isMedicationInItemTable, setIsMedicationInItemTable] = useState(false);

  // Directions options from new-script page
  const directionsOptions = [
    { value: "", label: "Select Directions" },
    { value: "Take 1 tablet daily", label: "Take 1 tablet daily" },
    { value: "Take 2 tablets twice daily", label: "Take 2 tablets twice daily" },
    {
      value: "Take 1 capsule three times daily",
      label: "Take 1 capsule three times daily",
    },
    { value: "Apply twice daily", label: "Apply twice daily" },
    { value: "As needed", label: "As needed" },
    { value: "Other", label: "Other" },
  ];

  // Initialize medications from initialMedications prop if available
  useEffect(() => {
    if (initialMedications && initialMedications.length > 0) {
      console.log("Setting initial medications from navigation state:", initialMedications);

      console.log("Current form medication value:", form.data.medication);

      setMedications(initialMedications);
    }
  }, [initialMedications]);

  // Debug: Check if form medication exists in medications array
  useEffect(() => {
    if (form.data.medication && medications.length > 0) {
      const found = medications.find((med) => med.name === form.data.medication);

      console.log("Medication check:", {
        formMedication: form.data.medication,
        medicationsAvailable: medications.map((m) => m.name),
        foundInDropdown: !!found,
      });

      // If form medication is not in the medications array, add it
      if (!found && form.data.medication.trim()) {
        console.log("Adding form medication to dropdown:", form.data.medication);

        const newMedication = {
          id: `form-med-${Date.now()}`,
          name: form.data.medication,
          quantity: form.data.quantity || "",
          packSize: form.data.packSize || "",
          barcode: form.data.barcode || "",
          icd10Codes: form.data.icd10 ? [form.data.icd10] : [],
        };

        setMedications((prev) => [...prev, newMedication]);
      }
    }
  }, [form.data.medication, medications]);

  // Auto-populate script number when prescriptionId is provided OR scriptNumber is available
  useEffect(() => {
    console.log("Auto-populate useEffect triggered:", {
      prescriptionId,
      scriptNumber,
      patientName: form.data.patientName,
      rxNumber: form.data.rxNumber,
      hasPatientName: !!form.data.patientName,
      hasRxNumber: !!form.data.rxNumber,
      hasScriptNumber: !!scriptNumber,
    });

    // Only auto-populate when we have a prescriptionId (from prescription workflow)
    // Don't auto-populate when coming from dispensary menu (normal flow)

    if (prescriptionId && form.data.patientName) {
      console.log("Conditions met, starting auto-population");

      // Set the selected patient based on the auto-filled patient name
      const autoPatient = {
        id: null,
        name: formatPatientNameForDisplay(form.data.patientName),
      };

      setSelectedPatient(autoPatient);

      // Create a mock RX number object for the dropdown - prioritize scriptNumber
      const autoRxNumber = {
        id: prescriptionId,
        displayValue: scriptNumber || form.data.rxNumber,
        pharmacistName: form.data.prescriber || "",
      };

      console.log("Created autoRxNumber:", autoRxNumber);

      setSelectedRxNumber(autoRxNumber);

      // Add current RX number to dropdown options immediately
      console.log("Setting rxNumbers to [autoRxNumber]");

      setRxNumbers([autoRxNumber]);

      // ICD-10 codes are already in form.data.icd10, no need to set state
      // The dropdown will use form.data.icd10 directly

      console.log("ICD-10 codes already in form data:", form.data.icd10);

      // Fetch RX numbers for this patient to populate the dropdown
      const fetchRxNumbersForAutoPatient = async () => {
        try {
          console.log("Fetching patient data for:", form.data.patientName);
          // We need to find the patient ID first to get their RX numbers

          const patientsResponse = await searchPatients(form.data.patientName);

          console.log("Patients response:", patientsResponse);

          if (patientsResponse.success && patientsResponse.data.length > 0) {
            const patient = patientsResponse.data[0];

            console.log("Found patient:", patient);

            setSelectedPatient(patient);

            const rxResponse = await getPatientRxNumbers(patient.id);

            console.log("RX numbers response:", rxResponse);

            if (rxResponse.success) {
              // Merge current RX number with patient's RX numbers
              const allRxNumbers = [
                autoRxNumber,
                ...rxResponse.data.filter(
                  (rx) => rx.displayValue !== (scriptNumber || form.data.rxNumber)
                ),
              ];

              console.log("Setting final rxNumbers:", allRxNumbers);

              setRxNumbers(allRxNumbers);

              // Ensure dropdown value is set to match the current RX number
              if (form.data.rxNumber !== (scriptNumber || autoRxNumber.displayValue)) {
                console.log(
                  "Updating form rxNumber to match scriptNumber:",
                  scriptNumber || autoRxNumber.displayValue
                );

                onFieldChange(
                  form.id,
                  "rxNumber",
                  scriptNumber || autoRxNumber.displayValue
                );
              }
            }
          } else {
            console.log("No patients found, keeping current RX number only");

            // IMPORTANT: Don't clear the current RX number from the dropdown

            // setRxNumbers([autoRxNumber]); // Already set above, don't clear it
          }
        } catch (error) {
          console.error("Error setting up auto-populated data:", error);

          // Keep the current RX number that was already set above

          // Don't clear it: setRxNumbers([autoRxNumber]);
        }
      };

      fetchRxNumbersForAutoPatient();
    } else {
      console.log("Conditions not met for auto-population");

      if (prescriptionId || scriptNumber) {
        console.log("PrescriptionId or scriptNumber exists but missing:", {
          hasPatientName: !!form.data.patientName,
          hasRxNumber: !!form.data.rxNumber,
          hasScriptNumber: !!scriptNumber,
        });
      }
    }
  }, [
    prescriptionId,
    scriptNumber,
    form.data.patientName,
    form.data.rxNumber,
    form.data.prescriber,
    form.data.icd10,
  ]);

  // Fetch RX numbers when a patient is selected
  useEffect(() => {
    const fetchRxNumbers = async () => {
      if (selectedPatient && selectedPatient.id) {
        try {
          const response = await getPatientRxNumbers(selectedPatient.id);
          if (response.success) {
            setRxNumbers(response.data);
          }
        } catch (error) {
          console.error("Error fetching RX numbers:", error);
          setRxNumbers([]);
        }
      } else {
        console.log("Second useEffect: clearing rxNumbers?", {
          hasPrescriptionId: !!prescriptionId,
          selectedPatientId: selectedPatient?.id,
          shouldClear: !prescriptionId,
        });

        // Only clear rxNumbers if not in auto-population mode

        // Don't clear if we have a prescriptionId (auto-population is active)

        if (!prescriptionId) {
          setRxNumbers([]);
        }
      }
    };

    fetchRxNumbers();
  }, [selectedPatient, prescriptionId]);

  // Reset ICD-10 visibility when medication changes
  useEffect(() => {
    if (selectedMedication && selectedMedication.name) {
      const checkItemTable = async () => {
        try {
          const result = await checkMedicationInItemTable(selectedMedication.name);

          setIsMedicationInItemTable(result.isInItemTable);

          console.log("ICD-10 visibility check:", {
            medication: selectedMedication.name,
            isInItemTable: result.isInItemTable,
            hideIcd10: result.isInItemTable,
          });
        } catch (error) {
          console.error("Error checking medication in item table:", error);
          setIsMedicationInItemTable(false);
        }
      };

      checkItemTable();
    } else {
      setIsMedicationInItemTable(false);
    }
  }, [selectedMedication]);

  // Fetch medications for selected RX number
  useEffect(() => {
    console.log("Medication fetch useEffect triggered:", {
      selectedRxNumber,
      selectedRxNumberId: selectedRxNumber?.id,
      prescriptionId,
      isFromDispensaryMenu,
    });

    const fetchMedications = async () => {
      // Fetch medications if we have a valid selected RX number with an ID
      // This works for both prescription workflow and normal flow from dispensary menu

      if (
        selectedRxNumber &&
        selectedRxNumber.id &&
        (typeof selectedRxNumber.id === "string"
          ? selectedRxNumber.id.trim() !== ""
          : selectedRxNumber.id > 0)
      ) {
        try {
          let response;

          if (prescriptionId) {
            // Prescription workflow: use prescription API

            response = await getPrescriptionMedications(selectedRxNumber.id);
          } else {
            // Normal flow from dispensary menu: use script API (works for both process and clinic scripts)

            console.log("Calling fetchScriptDetails with ID:", selectedRxNumber.id);

            response = await fetchScriptDetails(selectedRxNumber.id);

            console.log("Raw fetchScriptDetails response:", response);

            // Convert script details to medication format

            if (response && response.drugs && response.drugs.length > 0) {
              console.log("Script medications found:", response.drugs);

              console.log("First medication object:", response.drugs[0]);

              console.log(
                "Medication object keys:",
                Object.keys(response.drugs[0])
              );

              response = {
                success: true,
                data: response.drugs.map((med) => ({
                  id: med.id,
                  name:
                    med.name ||
                    med.medicationName ||
                    med.drugName ||
                    med.itemName ||
                    "Unknown Medication",
                  quantity: med.quantity,
                  packSize: med.packSize,
                  barcode: med.barcode,
                  directions: med.directions,
                })),
              };

              console.log("Mapped medications:", response.data);
            } else {
              console.log(
                "No script medications found, response structure:",
                response
              );

              console.log(
                "Script object keys:",
                response.script ? Object.keys(response.script) : "No script object"
              );

              console.log("Full response keys:", Object.keys(response));

              if (response.script) {
                console.log("Script object:", response.script);
              }

              response = { success: false, data: [] };
            }
          }

          console.log("Medications API response:", response);

          if (response.success) {
            console.log("Medications data:", response.data);

            setMedications(response.data);

            // Check item table for each medication to determine ICD-10 visibility

            for (const medication of response.data) {
              if (medication.name && medication.name === form.data.medication) {
                try {
                  const result = await checkMedicationInItemTable(medication.name);

                  setIsMedicationInItemTable(result.isInItemTable);

                  // Add SKU to form data if found
                  if (result.sku) {
                    onFieldChange(form.id, "sku", result.sku);
                  }

                  console.log("Prescription medication item table check:", {
                    medication: medication.name,
                    isInItemTable: result.isInItemTable,
                    sku: result.sku,
                  });

                  break; // Found the current medication, no need to check others
                } catch (error) {
                  console.error(
                    "Error checking prescription medication in item table:",
                    error
                  );

                  setIsMedicationInItemTable(false);
                }
              }
            }
          }
        } catch (error) {
          console.error("Error fetching medications:", error);
        }
      } else {
        console.log("Skipping medication fetch - no valid RX number selected");
      }
    };

    fetchMedications();
  }, [selectedRxNumber, form.data.medication]);

  // Handle medication selection
  const handleMedicationChange = async (e) => {
    const selectedMed = medications.find((med) => med.name === e.target.value);

    setSelectedMedication(selectedMed);

    // Check if medication is in item table

    if (selectedMed && selectedMed.name) {
      try {
        const result = await checkMedicationInItemTable(selectedMed.name);

        setIsMedicationInItemTable(result.isInItemTable);

        console.log("Medication item table check:", {
          medication: selectedMed.name,
          isInItemTable: result.isInItemTable,
          sku: result.sku,
        });

        // Set ICD-10 codes if available (only if not in item table)

        if (selectedMed.icd10Codes && !result.isInItemTable) {
          setIcd10Codes(selectedMed.icd10Codes);
        } else if (result.isInItemTable) {
          setIcd10Codes([]);
        }
      } catch (error) {
        console.error("Error checking medication in item table:", error);

        setIsMedicationInItemTable(false);
      }
    } else {
      setIsMedicationInItemTable(false);
    }

    // Auto-fill quantity and pack size

    if (selectedMed) {
      if (selectedMed.quantity) {
        onFieldChange(form.id, "quantity", selectedMed.quantity);
      }
      if (selectedMed.packSize) {
        onFieldChange(form.id, "packSize", selectedMed.packSize);
      }
    }

    // Set barcode from inventory table

    if (selectedMed) {
      // Try different possible barcode field names from inventory

      const inventoryBarcode =
        selectedMed.barcode ||
        selectedMed.sku ||
        selectedMed.inventoryBarcode ||
        selectedMed.itemBarcode;

      console.log(
        "Setting barcode field from inventory (MedicationFormCard):",
        inventoryBarcode
      );

      console.log("Selected medication object:", selectedMed);

      if (inventoryBarcode) {
        onFieldChange(form.id, "barcode", inventoryBarcode);
      } else {
        console.log(
          "No barcode found in selected medication (MedicationFormCard):",
          selectedMed
        );
      }
    }

    onFieldChange(form.id, "medication", e.target.value);
  };

  // Handle print functionality
  const handlePrint = (formId, showRepeatOverride = false) => {
    // Create print-specific styles
    const printStyles = document.createElement("style");
    printStyles.textContent = `
      @media print {
        /* Hide everything except our print label */
        body > *:not(.printOnlyLabel) {
          display: none !important;
        }
        
        /* Show only the print label and its content */
        .printOnlyLabel, .printOnlyLabel * {
          display: block !important;
          visibility: visible !important;
        }
        
        /* Position the label at the top of the page with no extra space */
        .printOnlyLabel {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          height: auto !important;
          background: white !important;
          padding: 0 !important;
          margin: 0 !important;
          box-sizing: border-box !important;
          overflow: hidden !important;
        }
        
        /* Ensure no page breaks and minimal height */
        .printOnlyLabel {
          page-break-inside: avoid !important;
          page-break-before: avoid !important;
          page-break-after: avoid !important;
          max-height: 100vh !important;
        }
        
        /* Remove all margins and padding from body and html */
        body, html {
          margin: 0 !important;
          padding: 0 !important;
          height: auto !important;
        }
        
        /* Minimize page margins */
        @page {
          size: auto;
          margin: 0mm !important;
        }
      }
      
      /* Hide the print-only label when not printing */
      @media screen {
        .printOnlyLabel {
          position: absolute !important;
          left: -9999px !important;
          top: -9999px !important;
          visibility: hidden !important;
          z-index: -9999 !important;
        }
      }
    `;

    // Create simple Item Label HTML
    const createItemLabelHTML = () => {
      const currentForm = form;
      const RIGHT_COL_WIDTH = "70px";
      const medicationName = currentForm?.data?.medication || "MEDICATION NAME";
      const directions = currentForm?.data?.directions || "Take as directed";
      const rxNumber = currentForm?.data?.rxNumber || "1416650";
      const patientName = currentForm?.data?.patientName || "PATIENT NAME";
      const practitionerName = currentForm?.data?.practitioner
        ? `${currentForm.data.practitioner.surname?.toUpperCase() || "PRACTITIONER"} ${currentForm.data.practitioner.firstname?.charAt(0)?.toUpperCase() || ""}.`
        : "DOCTOR";
      const currentDate = new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
      const dispenserName = user
        ? `${user.surname?.toUpperCase() || "DISPENSER"} ${user.name?.toUpperCase() || "NAME"}`
        : "DISPENSER NAME";

      // Build complete medication line with strength, dosage form, and pack size
      let medicationDetails = medicationName;

      const effectiveDrugInfo = currentForm?.data?.drugInfo || drugInfo || null;

      // Extract strength from medication name if no drug info available
      let extractedStrength = "";
      if (!effectiveDrugInfo?.strength && medicationName) {
        const strengthMatch = medicationName.match(
          /\b\d+(?:\.\d+)?\s?(?:mcg|µg|mg|g|ml|mL|IU)\b/i
        );
        if (strengthMatch) {
          extractedStrength = strengthMatch[0];
        }
      }

      // Extract dosage form from medication name if no drug info available
      let extractedDosageForm = "";
      if (!effectiveDrugInfo?.dosageForm && medicationName) {
        const forms = [
          /\btab(?:let)?s?\b/i,
          /\bcap(?:sule)?s?\b/i,
          /\bsuspension\b/i,
          /\bsyrup\b/i,
          /\bcream\b/i,
          /\bointment\b/i,
          /\binjection\b/i,
          /\bdrops\b/i,
          /\bsolution\b/i,
          /\belixir\b/i,
          /\bgel\b/i,
          /\blotion\b/i,
          /\bspray\b/i,
        ];
        const formMatch = forms.find((f) => f.test(medicationName));
        if (formMatch) {
          extractedDosageForm = formMatch.exec(medicationName)?.[0] || "";
        }
      }

      const strengthToShow = effectiveDrugInfo?.strength || extractedStrength;
      const dosageFormToShow = effectiveDrugInfo?.dosageForm || extractedDosageForm;
      const packSizeToShow =
        effectiveDrugInfo?.packSize || currentForm?.data?.packSize || "";

      // Add strength, dosage form, and pack size to medication details
      if (strengthToShow) medicationDetails += ` ${strengthToShow}`;
      if (dosageFormToShow) medicationDetails += ` ${dosageFormToShow}`;
      if (packSizeToShow) medicationDetails += ` ${packSizeToShow}`;

      const codeValue =
        repeatNumber != null && String(repeatNumber).trim() !== ""
          ? String(repeatNumber).trim()
          : "";

      const scheduleValue = effectiveDrugInfo?.schedule;
      const scheduleText =
        scheduleValue != null && String(scheduleValue).trim() !== ""
          ? ` S${scheduleValue}`
          : "";

      // Extract dependent code and main member ID from patient name
      const extractPatientCodes = (patientNameToExtract) => {
        if (!patientNameToExtract)
          return {
            dependentCode: "",
            mainMemberId: "",
            cleanName: patientNameToExtract,
          };

        // Look for pattern like "dependentcode/mainmemberid" at the end
        const codeMatch = patientNameToExtract.match(/(\d+)\/(\d+)$/);
        if (codeMatch) {
          const dependentCode = codeMatch[1];
          const mainMemberId = codeMatch[2];
          const cleanName = patientNameToExtract
            .replace(/\s*\d+\/\d+$/, "")
            .trim();
          return { dependentCode, mainMemberId, cleanName };
        }

        return {
          dependentCode: "",
          mainMemberId: "",
          cleanName: patientNameToExtract,
        };
      };

      const patientCodes = extractPatientCodes(patientName);

      return `
        <div class="printOnlyLabel" style="font-family: Helvetica, Condensed; font-size: 11px; line-height: 1.1; color: #000; padding: 0; margin: 0; font-weight: bold;">
          <div style="margin-bottom: 2px;">
            <span style="font-size: 12px; font-weight: 900;">${pharmacyInfo.name || "PHARMACY NAME"}</span><br>
            <span style="font-size: 10px; margin-top: -5px; display: block;">${pharmacyInfo.address || "ADDRESS"}</span><br>
            <span style="font-size:10px; margin-top: -4px; display: block;">${pharmacyInfo.phone || "PHONE"}</span>
          </div>
          
          <div style="border-bottom: 1px solid #000000ff; margin-bottom: 2px;"></div>
          
            <div style="margin-bottom: 3px; font-size: 11px; line-height: 1.4; white-space: nowrap; overflow: hidden;">
              ${medicationDetails}${codeValue ? ` (#${codeValue})` : ""}${scheduleText}
            </div>
          
          <div style="margin-bottom: 30px; font-size: 12px; line-height: 1.4;">
            ${directions}
          </div>
          
          ${showRepeatOverride ? `
          <div style="margin-bottom: 1px; font-size: 12px; line-height: 1.4;">
            Repeat ${repeatData?.displayText || ""}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp&nbsp;&nbsp;&nbsp;Rx : ${rxNumber}
          </div>
        ` : `
          <div style="margin-bottom: 1px; font-size: 12px; line-height: 1.4;">
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Rx : ${rxNumber}
          </div>
        `}
          
          <div style="margin-bottom: 1px; font-size: 12px; line-height: 1.4; white-space: nowrap; overflow: hidden;">
            ${patientCodes.cleanName}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${patientCodes.dependentCode}/${patientCodes.mainMemberId}
          </div>
          
          <div style="margin-bottom: 1px; font-size: 12px; line-height: 1.4; white-space: nowrap; overflow: hidden;">
            DOCTOR : ${practitionerName}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${currentDate}
          </div>
          
          <div style="margin-bottom: 0; font-size: 12px; line-height: 1.4;">
            DISPENSER : ${dispenserName}
          </div>
        </div>
      `;
    };

    // Create and append the label
    const labelDiv = document.createElement("div");
    labelDiv.innerHTML = createItemLabelHTML();
    const labelElement = labelDiv.firstElementChild;

    document.body.appendChild(labelElement);
    document.head.appendChild(printStyles);

    // Trigger print
    window.print();

    // Clean up after printing
    setTimeout(() => {
      if (document.body.contains(labelElement)) {
        document.body.removeChild(labelElement);
      }
      if (document.head.contains(printStyles)) {
        document.head.removeChild(printStyles);
      }
    }, 1000);
  };

  return {
    directionsOptions,
    formatPatientNameForDisplay,

    rxNumbers,
    selectedPatient,
    medications,
    selectedRxNumber,
    icd10Codes,
    selectedMedication,
    isMedicationInItemTable,

    setRxNumbers,
    setSelectedPatient,
    setMedications,
    setSelectedRxNumber,
    setIcd10Codes,
    setSelectedMedication,
    setIsMedicationInItemTable,

    handleMedicationChange,
    handlePrint,
    showRepeat,
  };
}
